import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, firstValueFrom, map, Observable, takeWhile, throwError} from 'rxjs';
import {environment} from '../../environments/environment';
import {SearchResult} from '../_models/_Index';
import {
  AiChatConversazione, AiChatConversazioneDettaglio, AiChatErrore, AiChatEvento, AiChatImpostazioni,
  AiChatImpostazioniRequest, AiChatModello, AiChatStato
} from '../_models/ai-chat.model';
import {SseEvento, SseHttpError, sseStream} from '../_helpers/sse-stream';
import {AuthenticationService} from './authentication.service';

export const AI_CHAT_PAGINA = 20;

/** Feature 159: API della chat AI su AgentApi (gateway `agent/`). Gli errori diventano `AiChatErrore`. */
@Injectable({providedIn: 'root'})
export class AiChatService {
  private readonly baseUrl = `${environment.localApiUrl}agent/ai-chat`;
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthenticationService);

  statoAsync(orgId: number): Promise<AiChatStato> {
    return this.richiesta(this.http.get<AiChatStato>(`${this.baseUrl}/stato/${orgId}`));
  }

  cercaAsync(orgId: number, search: string, page: number): Promise<SearchResult<AiChatConversazione>> {
    const body = {orgId, search, page, count: AI_CHAT_PAGINA, sortField: 'updatedAtUtc', sortAsc: false};
    return this.richiesta(this.http.post<SearchResult<AiChatConversazione>>(`${this.baseUrl}/conversazioni/cerca`, body));
  }

  creaAsync(orgId: number): Promise<AiChatConversazione> {
    return this.richiesta(this.http.post<AiChatConversazione>(`${this.baseUrl}/conversazioni`, {orgId}));
  }

  dettaglioAsync(id: string): Promise<AiChatConversazioneDettaglio> {
    return this.richiesta(this.http.get<AiChatConversazioneDettaglio>(`${this.baseUrl}/conversazioni/${id}`));
  }

  rinominaAsync(id: string, titolo: string): Promise<void> {
    return this.richiesta(this.http.put<void>(`${this.baseUrl}/conversazioni/${id}`, {titolo}));
  }

  eliminaAsync(id: string): Promise<void> {
    return this.richiesta(this.http.delete<void>(`${this.baseUrl}/conversazioni/${id}`));
  }

  modelliAsync(): Promise<AiChatModello[]> {
    return this.richiesta(this.http.get<AiChatModello[]>(`${this.baseUrl}/modelli`));
  }

  impostazioniAsync(orgId: number): Promise<AiChatImpostazioni> {
    return this.richiesta(this.http.get<AiChatImpostazioni>(`${this.baseUrl}/impostazioni/${orgId}`));
  }

  salvaImpostazioniAsync(orgId: number, request: AiChatImpostazioniRequest): Promise<AiChatImpostazioni> {
    return this.richiesta(this.http.put<AiChatImpostazioni>(`${this.baseUrl}/impostazioni/${orgId}`, request));
  }

  /** Invia la domanda e restituisce gli eventi della risposta; annullare la sottoscrizione interrompe. */
  invia(conversazioneId: string, orgId: number, testo: string): Observable<AiChatEvento> {
    const url = `${this.baseUrl}/conversazioni/${conversazioneId}/messaggi`;
    const token = this.authService.currentUserValue?.jwtToken;
    return sseStream({url, method: 'POST', token, body: {orgId, testo}}).pipe(
      map(aEvento),
      takeWhile(evento => evento.type !== 'done' && evento.type !== 'error', true),
      catchError((errore: unknown) => throwError(() => aErrore(errore))));
  }

  private richiesta<T>(chiamata: Observable<T>): Promise<T> {
    return firstValueFrom(chiamata.pipe(catchError((errore: unknown) => throwError(() => aErrore(errore)))));
  }
}

function aEvento(evento: SseEvento): AiChatEvento {
  return {type: evento.type, ...JSON.parse(evento.data)} as AiChatEvento;
}

const MESSAGGI_HTTP: Record<number, string> = {
  0: 'Assistente non raggiungibile: controlla la connessione.',
  401: 'Sessione scaduta: accedi di nuovo.',
  403: "Non hai i permessi per usare l'assistente.",
  404: 'Conversazione non trovata.'
};

/** Converte un errore HTTP o di stream nella forma `{status, codice?, messaggio}`. */
export function aErrore(errore: unknown): AiChatErrore {
  if (errore instanceof HttpErrorResponse) return daCorpo(errore.status, errore.error);
  if (errore instanceof SseHttpError) return daCorpo(errore.status, leggiJson(errore.body));
  return {status: 0, messaggio: MESSAGGI_HTTP[0]};
}

function daCorpo(status: number, corpo: unknown): AiChatErrore {
  const dati = (corpo && typeof corpo === 'object' ? corpo : {}) as Partial<AiChatErrore>;
  const messaggio = dati.messaggio ?? MESSAGGI_HTTP[status] ?? `Errore dell'assistente (HTTP ${status}).`;
  return {status, codice: dati.codice, messaggio, azzeramentoUtc: dati.azzeramentoUtc};
}

function leggiJson(testo: string): unknown {
  try {
    return JSON.parse(testo);
  } catch {
    return null;
  }
}
